<script setup>
import axios from "axios";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { useToast } from "vue-toastification";

const props = defineProps(["isDialogOpen", "documentId", "documentName"]);
const emit = defineEmits(["closeDialog", "refreshDocuments"]);
const router = useRouter();
const toast = useToast();
const { t } = useI18n();

const deleteDocument = async () => {
  try {
    await axios.delete(`/api/documents/${props.documentId}`);
    emit("refreshDocuments");
    emit("closeDialog");
    toast.success(t("documents.delete_success"));
  } catch (error) {
    console.error(error);
  }
};
</script>

<template>
  <v-dialog v-model="props.isDialogOpen" max-width="480" persistent>
    <v-card class="delete-dialog-card" elevation="2" rounded="lg">
      <v-card-title class="pa-6 pb-4">
        <div class="title-container">
          <div class="icon-wrapper">
            <v-icon size="20" color="text">mdi-alert-outline</v-icon>
          </div>
          <div class="title-text">
            <span class="dialog-title">{{ $t("documents.delete_confirmation_title") }}</span>
          </div>
        </div>
      </v-card-title>

      <v-card-text class="pa-6 pt-0">
        <div class="warning-content">
          <div class="document-name-box mb-4">
            <v-icon size="20" class="mr-3" color="default">mdi-file-document-outline</v-icon>
            <span class="document-name">{{ documentName }}</span>
          </div>
          <p class="mb-4 text-body-2 description-text">
            {{ $t("documents.delete_document_description") }}
          </p>
          <div class="warning-box">
            <v-icon size="16" class="mr-2" color="text">mdi-alert-circle</v-icon>
            <span class="warning-text">{{ $t("documents.delete_irreversible_warning") }}</span>
          </div>
        </div>
      </v-card-text>

      <v-card-actions class="action-section pa-6 pt-4">
        <v-spacer></v-spacer>
        <v-btn
          variant="outlined"
          color="default"
          @click="emit('closeDialog')"
          class="mr-3 cancel-button"
          rounded="lg"
        >
          {{ $t("common.cancel") }}
        </v-btn>
        <v-btn
          variant="flat"
          @click="deleteDocument"
          prepend-icon="mdi-delete"
          rounded="lg"
          class="confirm-button"
        >
          {{ $t("common.confirm") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped lang="scss">
.delete-dialog-card {
  overflow: hidden;
  background: white;
}

.title-container {
  display: flex;
  align-items: center;
  gap: 16px;
}

.icon-wrapper {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #d1ee76;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.title-text {
  flex: 1;
}

.dialog-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #083957;
  line-height: 1.4;
}

.document-name-box {
  display: flex;
  align-items: center;
  background-color: #F7F7F7;
  border: 1px solid #EEEDEB;
  border-radius: 8px;
  padding: 14px 18px;
  
  .document-name {
    font-weight: 500;
    color: #083957;
    font-size: 0.95rem;
    word-break: break-word;
    flex: 1;
  }
}

.description-text {
  color: #698680;
  line-height: 1.6;
}

.warning-box {
  display: flex;
  align-items: center;
  background: rgba(209, 238, 118, 0.12);
  border-left: 3px solid #d1ee76;
  padding: 10px 14px;
  border-radius: 4px;
  
  .warning-text {
    font-size: 0.875rem;
    color: #698680;
    font-weight: 500;
  }
}

.action-section {
  background-color: #FAFAFA;
  border-top: 1px solid #EEEDEB;
}

.v-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: 0.01em;
  padding: 0 20px;
}

.confirm-button {
  background: #0E25B1 !important;
  color: white !important;

  &:hover {
    background: #0A1D8F !important;
  }
}

.cancel-button {
  &:hover {
    background: rgba(14, 37, 177, 0.08) !important;
    border-color: #0E25B1 !important;
    color: #0E25B1 !important;
  }
}
</style>
